import type { WebhookLimits, WebhookSampleIdentity } from '@/services/webhookDelivery';

/**
 * The webhook integration guide (issue #7255; the vault's webhook delivery PRD, rev. 5.3, "Integration
 * guide on the tab"): the merchant-facing contract, in six parts, in the PRD's order. ONE structured source: the tab
 * renders it and "Download integration guide" serialises it (renderGuideMarkdown), so there is no second copy of the
 * text. Every rule number is read from the backend's `limits` object (which a backend test proves equal to
 * WEBHOOK_RULES); none is written here. PRD rev. 5.4: no store is named in this module — the header examples are the
 * selected store's own sample product (GET /webhook-delivery/sample-identity), or neutral placeholders when it has none.
 */

export interface GuideStore {
  siteId: string;
  name: string;
}

export interface GuideSchema {
  version: string;
  sha256: string;
}

/** The store's sample product as its headers carry it (sample-identity); null when the store has no exportable product. */
export type GuideIdentity = WebhookSampleIdentity;

export type GuideBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'table'; columns: string[]; rows: string[][] };

export interface GuidePart {
  title: string;
  blocks: GuideBlock[];
}

export interface IntegrationGuide {
  title: string;
  parts: GuidePart[];
}

export const SCHEMA_FILE_NAME = 'pagemind-enriched-product.schema.json';

/** The header examples when the store has no exportable product (PRD rev. 5.4; rev. 5.6: one call per product). */
export const PLACEHOLDER_EXAMPLES = Object.freeze({ sku: 'SKU-12345', itemGroupId: 'GROUP-100', store: 'Your store', granularity: 'product' as const });

/** "Schema 1.1 · sha256 a5de44e4f4e0": the schema by version and the first 12 characters of its SHA-256 (PRD rev. 5.4). */
export const schemaLabel = (schema: GuideSchema) => `Schema ${schema.version} · sha256 ${schema.sha256.slice(0, 12)}`;

/**
 * The export API request that returns the same product as a webhook call (DELIVERY_SHAPE_V1 as the route's query
 * string; PRD rev. 5.4 "P4 end-to-end test", "Parity with the export API": 150 of 150 byte-identical on P4).
 */
export const MATCHING_EXPORT_QUERY = 'format=json&aeoRowFormat=structured&includeProductAEO=true&includeProductSummary=true'
  + '&includeComparisonData=true&includeProductSpecifications=true&includeCategoryAEO=true&includeProductFaqs=true'
  + '&includeReviewerInfo=true&include_section_versions=true&aeoLanguage=all&aeoStatus=approved&filterByAeoStatus=true'
  + '&requireCompleteTranslations=true&includeUnavailable=true&markAsFetched=false';
export const matchingExportRequest = (siteId: string) => `GET /api/sites/${siteId}/categories/all/export/enriched-feed?${MATCHING_EXPORT_QUERY}`;

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const hoursText = (h: number) => count(h, 'hour', 'hours');
const minutesText = (m: number) => (m >= 60 && m % 60 === 0 ? hoursText(m / 60) : count(m, 'minute', 'minutes'));
const secondsText = (s: number) => (s >= 60 && s % 60 === 0 ? minutesText(s / 60) : count(s, 'second', 'seconds'));
/** "every hour", "every 30 minutes", "every 2 hours" */
const every = (m: number) => `every ${minutesText(m).replace(/^1 /, '')}`;
/** "1, 2, 5 and 10" */
const series = (ns: number[]) => (ns.length <= 1 ? ns.join('') : `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`);
/** "once", "twice", "three times" … "ten times", then "12 times" */
const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const timesText = (n: number) => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : n} times`);
const megabytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;
const p = (text: string): GuideBlock => ({ kind: 'paragraph', text });

/**
 * The guide for one store, from the status `limits` and `schema` the tab already receives, and the store's sample
 * identity for the header examples (null: placeholders).
 */
export function buildIntegrationGuide(limits: WebhookLimits, store: GuideStore, schema: GuideSchema, identity: GuideIdentity | null): IntegrationGuide {
  const l = limits;
  const example = identity ?? PLACEHOLDER_EXAMPLES;
  return {
    title: `PageMind webhook integration guide: ${store.name}`,
    parts: [
      {
        title: '1. What your endpoint must do',
        blocks: [
          p('Accept a POST over HTTPS, on port 443 or the port written in your URL, with a certificate signed by a public certificate authority.'),
          p(`Each call carries one product as JSON. A body can be up to about ${megabytes(l.maxRequestBytes)}.`),
          // rev. 5.7: the endpoint's own response timeout, in seconds as the tab sets it ("within 60 seconds", not "1 minute")
          p(`Answer with any 2xx status within ${count(l.requestTimeoutSeconds, 'second', 'seconds')}. A slower answer counts as a failed call.`),
          p('Redirects are not followed: give us the URL that answers, not one that redirects.'),
          p('Apply the updates of each product in the order you receive them. You can answer first and process later, as long as the updates of each product stay in that order (one queue or lock per product). Processing the updates of one product in parallel can apply an older one last.'),
        ],
      },
      {
        title: '2. What we send',
        blocks: [
          p('One POST per product. The body is one product, exactly as the export API returns it, with no wrapper. Every call carries these headers:'),
          {
            kind: 'table',
            columns: ['Header', 'Example', 'What it is'],
            rows: [
              ['Content-Type', 'application/json', 'Always JSON.'],
              ['Content-Encoding', 'gzip', 'Only if compression is turned on for your endpoint (off by default).'],
              ['User-Agent', 'PageMind-Webhook/1', 'Always the same.'],
              ['X-PageMind-Event', 'product.updated', 'product.updated for an hourly change, a test product and a resend; product.full_export for a call of a full export.'],
              ['X-PageMind-Delivery-Id', '3f1c9a52-7d4e-4b8a-9c61-0e2f5b7d8a14', 'The queued delivery. It stays the same across our retries of it. Quote it when you contact us; it is not a deduplication key.'],
              ['X-PageMind-Site-Id', store.siteId, 'This store in PageMind.'],
              ['X-PageMind-Store', example.store, 'The store name in PageMind. A value that is not plain printable ASCII (store name, sku or item group) is percent-encoded.'],
              ['X-PageMind-Sku', example.sku, 'The product\'s sku.'],
              ['X-PageMind-Item-Group-Id', example.itemGroupId, 'The product\'s item_group_id. Always present; empty when the product has none.'],
              // rev. 5.6 part 1 ("Only send canonicals")
              ['X-PageMind-Granularity', example.granularity, 'model when your endpoint sends canonicals only: one call per model, with its representative product. product otherwise: one call per product.'],
              ['X-PageMind-Shape-Version', String(l.shapeVersion), 'The version of the product shape.'],
              ['X-PageMind-Payload-Hash', '9b2e4f0c7a1d3e5f8b6c2a4d1e0f9c8b7a6d5e4f3c2b1a0918273645a0b1c2d3', 'SHA-256 (hex) of the uncompressed body of this call.'],
              ['X-PageMind-Run-Id', '6a0e3b9f-2c4d-4e1a-8f7b-5d9c1e2a3b4c', 'Which of our runs queued the product. For support only: do not order or filter updates by it.'],
              ['X-PageMind-Run-Seq', '12', 'That run\'s sequence number. For support only.'],
              ['X-PageMind-Timestamp', '1791712800', 'When the call was sent, in unix seconds.'],
              ['Your authentication header', 'Authorization: Bearer <your token>', 'Exactly as you entered it (see part 5).'],
            ],
          },
          // the two layers, in the PRD's words (rev. 5.4)
          p('Your own feed columns are passed through exactly as you provide them; PageMind\'s fields are the ones named aeo_…, plus sku, item_group_id and section_versions. Only PageMind\'s fields are governed by the schema.'),
          p(`The body is defined by the JSON schema ${SCHEMA_FILE_NAME}: ${schemaLabel(schema)} (SHA-256 ${schema.sha256}).`),
          p(`The export API returns the same product, in the same shape, for this request: ${matchingExportRequest(store.siteId)}`),
          p('On the Webhook tab of the Feed Export page, Download JSON schema gives that file, and Download a sample product gives one product of this store, rendered now in the exact shape a call carries, with the headers a call carries (X-PageMind-Event: product.sample, without Run-Id and Run-Seq).'),
        ],
      },
      {
        title: '3. How we read your answers',
        blocks: [
          {
            kind: 'table',
            columns: ['Your answer', 'What PageMind does'],
            rows: [
              ['2xx', 'Delivered.'],
              ['No answer or a temporary error: timeouts, connection, DNS and TLS errors, 408, 429, 5xx', `We treat it as your endpoint being unavailable, not as a problem of the product: the product is not marked failed and nothing queued is lost. After ${count(l.circuitConsecutiveFailures, 'failure', 'failures')} in a row, or more than ${percent(l.circuitFailureRatio)} of at least ${count(l.circuitMinSamples, 'call', 'calls')} in ${secondsText(l.circuitWindowSeconds)}, we pause calls and send one at a time, waiting ${series(l.probeDelaysMinutes)} minutes between them, then ${every(l.probeSteadyMinutes)}. The first success resumes delivery. If nothing succeeds for ${hoursText(l.circuitPauseHours)}, the webhook is paused and PageMind's operators are alerted.`],
              ['Retry-After', `Honoured, in seconds or as an HTTP date, up to ${secondsText(l.retryAfterMaxSeconds)}.`],
              ['401 or 403', 'The webhook is paused immediately and PageMind\'s operators are alerted. Replace the authentication in PageMind, then resume from the Webhook tab.'],
              // PRD rev. 5.6 part 2: a definitive answer is never retried (no row backoff)
              ['Any other 4xx (400, 404, 409, 410, 422…) or a 3xx', 'A definitive answer about that product: we do not retry it. A 3xx is not followed. The call is marked failed in the delivery log with your response, so you can fix the cause and send it again with Resend; the product is also sent again by itself the next time it changes.'],
            ],
          },
          // PRD rev. 5.8: a 5xx that only one product gets, while the endpoint accepts others, is about that product
          p(`If your endpoint answers a 5xx to one product ${timesText(l.perProductEndpointFailureLimit)} while accepting others, we stop retrying that product and mark it failed for review.`),
        ],
      },
      {
        title: '4. When calls arrive',
        blocks: [
          p(`Changes: ${every(l.deltaIntervalMinutes)}, starting when you enable the webhook, we send every product that changed since the previous round, whether it existed before you enabled or is new. Enabling does not backfill: a product that does not change after you enable is not sent until you request a full export.`),
          p(`Full export: every product of this store, only when you request one, at most once every ${hoursText(l.fullSyncMinIntervalHours)}.`),
          p(`Pace: calls go out at the pace you set, by default ${l.maxParallel.default} in parallel and ${l.maxPerMinute.default} per minute (you can set ${l.maxParallel.min} to ${l.maxParallel.max} in parallel and ${l.maxPerMinute.min} to ${l.maxPerMinute.max} per minute). They arrive in bursts: up to the per-minute limit within a few seconds, then nothing until the minute has passed. Size your endpoint for the burst, or lower the limit.`),
          p('There are no delete events: a product that stops being exportable simply stops arriving.'),
          p('The same product can arrive more than once, for example when we did not receive your answer. Applying it again is harmless: it is an ordinary update with the product\'s current content.'),
          p('A product that fails PageMind\'s own payload contract check is held on our side and not sent. It is sent once the defect is fixed.'),
          // rev. 5.6 part 1, decisions D1 and D2 in the merchant's words
          p('With canonicals only, we send one call per model, with its representative product. A change to another variant of the model (its own price or stock) does not trigger a call, because the body we would send is the same. Key your records on X-PageMind-Item-Group-Id when it is not empty, otherwise on X-PageMind-Sku, and keep the two in separate namespaces so a group id and a SKU can never collide. The representative product of a group can change over time; the group id does not.'),
          // Pau, PR #7309 round 2: the option governs what PageMind queues on its own, not what you send by hand
          p('Resend and the test product send the product you choose, in any mode. With canonicals only, a resent variant carries the same generated content as its model; only its own id, GTIN and price differ.'),
        ],
      },
      {
        title: '5. Authentication',
        blocks: [
          p('You provide one header name and value (for example Authorization, or an API-key header), or a username and password for Basic auth.'),
          p('PageMind sends it on every call exactly as you entered it. If your endpoint expects a scheme, include it yourself: enter Bearer <your token>, not only the token.'),
          p(`A value shorter than ${count(l.auth.minSecretChars, 'character', 'characters')} (not counting a Bearer, Basic or Token scheme) is refused.`),
          p(`The value is stored encrypted and never shown in full again: at most its last ${count(l.auth.maskSuffixChars, 'character', 'characters')}, and only when it has at least ${count(l.auth.maskSuffixMinChars, 'character', 'characters')} (never for Basic).`),
          p('PageMind issues no credential and does not sign calls. Check the sender by your header, and the body by X-PageMind-Payload-Hash if you want to.'),
        ],
      },
      {
        title: '6. Test before going live',
        blocks: [
          p('Enable the webhook with your endpoint URL and authentication.'),
          p('Click Send test product, then check its line in the delivery log and your server\'s log.'),
          p('When you are ready, click Request full export to send every product of the store. The changes have been sent since you enabled.'),
        ],
      },
    ],
  };
}

const cell = (s: string) => s.replace(/\|/g, '\\|');

/** The same guide as a Markdown file, for the engineer who has no admin access. */
export function renderGuideMarkdown(guide: IntegrationGuide): string {
  const out: string[] = [`# ${guide.title}`];
  for (const part of guide.parts) {
    out.push('', `## ${part.title}`);
    for (const block of part.blocks) {
      out.push('');
      if (block.kind === 'paragraph') {
        out.push(block.text);
      } else {
        out.push(`| ${block.columns.map(cell).join(' | ')} |`, `|${block.columns.map(() => '---').join('|')}|`);
        for (const row of block.rows) out.push(`| ${row.map(cell).join(' | ')} |`);
      }
    }
  }
  return `${out.join('\n')}\n`;
}

/** pagemind-webhook-integration-guide-<store-slug>.md; a store whose name has no letter or digit is named by its id. */
export function guideFileName(store: GuideStore): string {
  const slug = store.name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `pagemind-webhook-integration-guide-${slug || store.siteId}.md`;
}
